// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import React, { useRef, useEffect, useState } from 'react';
import { getLocator } from '@tcp/core/src/utils';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import { imgVideoData } from '../../ModuleContainer/molecules/ImageComponent/view/imageComponentHelper';
import { Container, Card, Media, Wrapper, FadeOverlay } from '../styles/ImageCardAutoScroll.style';

const MIN_CLONE_COUNT = 2;

export const getDisableVideosOnAppleValue = (
  disableVideosForAppleDevice,
  disableVideosForAppleDeviceValue
) => disableVideosForAppleDevice || disableVideosForAppleDeviceValue;

const ImageCardAutoScroll = ({
  speed,
  mediaData,
  isPreload,
  isEnableMute,
  mediaWidthVal,
  aspectRatioObj,
  imageTileSpace,
  assignKeyValue,
  moduleAnalyticsTag,
  useRouterNavigation,
  autoScrollDirection,
  imageCardDesignHidden,
  mediaBorderRadiusOverride,
  disableVideosForAppleDevice,
}) => {
  const widthVal = !isEmpty(mediaWidthVal) ? mediaWidthVal : Math.ceil(100 / mediaData.length);
  const { extraConfig } = React.useContext(LabelsContext) || {};
  const { isDamImageV2Enabled, disableVideosForAppleDeviceValue } = extraConfig || {};

  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [clonedList, setClonedList] = useState([]);

  useEffect(() => {
    const cloneCount = Math.max(MIN_CLONE_COUNT, Math.ceil(24 / mediaData.length));
    setClonedList(
      Array(cloneCount)
        .fill(mediaData)
        .reduce((acc, val) => acc.concat(val), [])
    );
  }, [mediaData]);

  useEffect(() => {
    const container = containerRef.current;

    const scrollStep = autoScrollDirection === 'right' ? -speed : speed;
    let animationFrame;

    const shouldLoopToStart = () =>
      Math.floor(container.scrollLeft) >= container.scrollWidth - container.clientWidth - 1;

    const shouldLoopToEnd = () =>
      Math.ceil(container.scrollLeft) <= 0 && autoScrollDirection === 'right';

    const applyScrollLogic = () => {
      container.scrollLeft += scrollStep;

      if (shouldLoopToStart()) {
        container.scrollLeft = 1;
      } else if (shouldLoopToEnd()) {
        container.scrollLeft = container.scrollWidth / 2;
      }
    };

    const scroll = () => {
      if (!isPaused) {
        applyScrollLogic();
      }
      animationFrame = requestAnimationFrame(scroll);
    };

    animationFrame = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationFrame);
  }, [isPaused, autoScrollDirection, speed, clonedList]);

  // Touch support
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return undefined;

    let startX = 0;
    let scrollStart = 0;

    const onTouchStart = (e) => {
      setIsPaused(true);
      startX = e.touches[0].pageX;
      scrollStart = container.scrollLeft;
    };

    const onTouchMove = (e) => {
      const deltaX = e.touches[0].pageX - startX;
      container.scrollLeft = scrollStart - deltaX;
    };

    const onTouchEnd = () => setIsPaused(false);

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const renderMedia = (item, index) => {
    const { image, video, imageMobile, videoMobile, singleCTAButton } = item;
    const { imgData, videoData } = imgVideoData({
      image,
      mediaBorderRadiusOverride,
      video,
      imageMobile,
      videoMobile,
    });

    return (
      <Card
        key={`media-auto-scroll-tile-${index}`}
        imageCardDesignHidden={imageCardDesignHidden}
        aspectRatioObj={aspectRatioObj}
        className={`media-auto-scroll-tile-${index}`}
        widthVal={widthVal}
      >
        <Media
          imgData={imgData}
          dataLocator={`${getLocator('media-auto-Scroll')}${index + 1}`}
          className="media-auto-scroll-content"
          link={singleCTAButton}
          videoData={videoData}
          isOptimizedVideo
          isHomePage
          lazyLoad
          isModule
          disableVideosForAppleDevice={getDisableVideosOnAppleValue(
            disableVideosForAppleDevice,
            disableVideosForAppleDeviceValue
          )}
          preloadImage={isPreload}
          aspectRatioObj={aspectRatioObj}
          isDamImageV2Enabled={isDamImageV2Enabled}
          assignKeyValue={assignKeyValue}
          isEnableMute={isEnableMute}
          useRouterNavigation={useRouterNavigation}
          moduleAnalyticsTag={moduleAnalyticsTag}
        />
      </Card>
    );
  };

  return (
    <Wrapper
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="media-auto-scroll-wrapper"
    >
      <FadeOverlay className="fade-overlay-left" left />
      <FadeOverlay className="fade-overlay-right" />
      <Container ref={containerRef} imageTileSpace={imageTileSpace}>
        {clonedList.map(renderMedia)}
      </Container>
    </Wrapper>
  );
};

ImageCardAutoScroll.propTypes = {
  speed: PropTypes.number,
  isPreload: PropTypes.bool,
  isEnableMute: PropTypes.bool,
  imageTileSpace: PropTypes.string,
  useRouterNavigation: PropTypes.bool,
  aspectRatioObj: PropTypes.shape({}),
  moduleAnalyticsTag: PropTypes.string,
  imageCardDesignHidden: PropTypes.bool,
  disableVideosForAppleDevice: PropTypes.bool,
  assignKeyValue: PropTypes.string.isRequired,
  mediaBorderRadiusOverride: PropTypes.shape({}),
  mediaData: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      video: PropTypes.string,
      imageMobile: PropTypes.string,
      videoMobile: PropTypes.string,
      singleCTAButton: PropTypes.shape({
        url: PropTypes.string,
        text: PropTypes.string,
      }),
    })
  ).isRequired,
  autoScrollDirection: PropTypes.oneOf(['left', 'right']),
};

ImageCardAutoScroll.defaultProps = {
  speed: 1,
  isPreload: false,
  isEnableMute: true,
  aspectRatioObj: {},
  moduleAnalyticsTag: '',
  imageTileSpace: '16px',
  useRouterNavigation: false,
  autoScrollDirection: 'left',
  imageCardDesignHidden: false,
  mediaBorderRadiusOverride: {},
  disableVideosForAppleDevice: false,
};

export default ImageCardAutoScroll;
export { ImageCardAutoScroll as ImageCardAutoScrollVanilla };
